import { createFileRoute, Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useCallback, useEffect, useRef, useState } from "react";
import { useAccount } from "@/hooks/use-account";
import { recordUploadNotification } from "@/lib/notifications";
import {
  createUploadUrl,
  createDownloadUrl,
  deleteStoredObject,
  listStoredObjects,
} from "@/lib/storage.functions";
import {
  ALLOWED_UPLOAD_GROUPS,
  MAX_UPLOAD_BYTES,
  UPLOAD_ACCEPT_ATTRIBUTE,
  formatBytes,
  validateUploadFile,
  type UploadRejection,
} from "@/lib/upload-validation";
import {
  describeUploadFailure,
  uploadWithProgress,
  type UploadFailure,
  type UploadProgress,
} from "@/lib/upload-transfer";

export const Route = createFileRoute("/_authenticated/files")({
  head: () => ({
    meta: [
      { title: "File storage | Finendra" },
      {
        name: "description",
        content: "Upload, preview and remove files stored in Finendra cloud storage.",
      },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: FilesPage,
});

type StoredObject = { key: string; size: number; lastModified: string | null };

type SortKey = "key-asc" | "key-desc" | "newest" | "oldest" | "largest" | "smallest";

const SORT_LABELS: { value: SortKey; label: string }[] = [
  { value: "key-asc", label: "Key (A–Z)" },
  { value: "key-desc", label: "Key (Z–A)" },
  { value: "newest", label: "Newest first" },
  { value: "oldest", label: "Oldest first" },
  { value: "largest", label: "Largest first" },
  { value: "smallest", label: "Smallest first" },
];

const SAFE_KEY = /[^A-Za-z0-9!_.*'()-]/g;

function formatDate(value: string | null) {
  if (!value) return "—";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "—" : date.toLocaleString();
}

function FilesPage() {
  const account = useAccount();
  const listFiles = useServerFn(listStoredObjects);
  const getUploadUrl = useServerFn(createUploadUrl);
  const getDownloadUrl = useServerFn(createDownloadUrl);
  const removeFile = useServerFn(deleteStoredObject);

  const inputRef = useRef<HTMLInputElement>(null);
  const abortRef = useRef<AbortController | null>(null);
  const [objects, setObjects] = useState<StoredObject[]>([]);
  const [prefix, setPrefix] = useState("");
  const [query, setQuery] = useState("");
  const [sort, setSort] = useState<SortKey>("key-asc");
  const [pageSize, setPageSize] = useState(25);
  const [page, setPage] = useState(1);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [totalKnown, setTotalKnown] = useState<number | null>(null);
  const [moreAfterPage, setMoreAfterPage] = useState(false);
  const [capped, setCapped] = useState(false);
  const cursorsRef = useRef<(string | null)[]>([null]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [rejection, setRejection] = useState<UploadRejection | null>(null);
  const [transfer, setTransfer] = useState<UploadProgress | null>(null);
  const [failure, setFailure] = useState<UploadFailure | null>(null);
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [copied, setCopied] = useState<{ key: string; label: string } | null>(null);
  const [copyingLink, setCopyingLink] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<StoredObject | null>(null);
  const copiedTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [listError, setListError] = useState<string | null>(null);
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  const lastRequestRef = useRef<{
    page: number;
    overrides?: { prefix?: string; sort?: SortKey; pageSize?: number; q?: string } | undefined;
  }>({ page: 1 });

  // Monotonic request id + abort controller: rapid paging/sorting must never let
  // an older listing response overwrite a newer one.
  const listRequestRef = useRef(0);
  const listAbortRef = useRef<AbortController | null>(null);

  const load = useCallback(
    async (
      targetPage: number,
      overrides?: { prefix?: string; sort?: SortKey; pageSize?: number; q?: string },
    ) => {
      const activeSort = overrides?.sort ?? sort;
      const activeSize = overrides?.pageSize ?? pageSize;
      const activePrefix = (overrides?.prefix ?? prefix).trim() || undefined;
      const activeQuery = (overrides?.q ?? query).trim() || undefined;

      // Cancel whatever listing request is still in flight.
      listAbortRef.current?.abort();
      const controller = new AbortController();
      listAbortRef.current = controller;
      const requestId = ++listRequestRef.current;
      const isCurrent = () => requestId === listRequestRef.current;

      lastRequestRef.current = { page: targetPage, overrides };
      setLoading(true);
      setListError(null);
      try {
        const result = await listFiles({
          signal: controller.signal,
          data: {
            prefix: activePrefix,
            q: activeQuery,
            sort: activeSort,
            pageSize: activeSize,
            page: targetPage,
            cursor: cursorsRef.current[targetPage - 1] ?? null,
          },
        });
        if (!isCurrent()) return; // A newer request already won.
        setObjects(result.objects);
        setNextCursor(result.nextCursor);
        setTotalKnown(result.totalKnown);
        setMoreAfterPage("hasMore" in result ? Boolean(result.hasMore) : false);
        setCapped(result.capped);
        if (result.mode === "cursor" && result.nextCursor) {
          cursorsRef.current[targetPage] = result.nextCursor;
        }
        setPage(targetPage);
        setHasLoadedOnce(true);
      } catch (cause) {
        // Superseded or deliberately aborted requests are not errors.
        if (!isCurrent() || controller.signal.aborted) return;
        // Keep the previously rendered page visible and surface a retryable banner.
        setListError(
          cause instanceof Error && cause.message
            ? cause.message
            : "Could not load files from storage.",
        );
      } finally {
        // Only the newest request owns the spinner, so it never clears early.
        if (isCurrent()) setLoading(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [prefix, query, sort, pageSize],
  );

  const retryLoad = useCallback(() => {
    const last = lastRequestRef.current;
    void load(last.page, last.overrides);
  }, [load]);

  const refresh = useCallback(
    async (overrides?: { prefix?: string; sort?: SortKey; pageSize?: number; q?: string }) => {
      cursorsRef.current = [null];
      await load(1, overrides);
    },
    [load],
  );

  const hasNext =
    totalKnown === null ? Boolean(nextCursor) || moreAfterPage : page * pageSize < totalKnown;

  useEffect(() => {
    if (!account.loading) void refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [account.loading]);

  // Debounced filename search: one request per pause in typing, always page 1.
  const firstQueryRun = useRef(true);
  useEffect(() => {
    if (firstQueryRun.current) {
      firstQueryRun.current = false;
      return;
    }
    if (account.loading) return;
    const timer = setTimeout(() => void refresh({ q: query }), 300);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, account.loading]);

  // Cancel any in-flight upload/listing and pending copy feedback if the page unmounts.
  useEffect(
    () => () => {
      abortRef.current?.abort();
      listAbortRef.current?.abort();
      listRequestRef.current += 1;
      if (copiedTimer.current) clearTimeout(copiedTimer.current);
    },

    [],
  );

  // Escape closes the delete confirmation.
  useEffect(() => {
    if (!deleteTarget) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") setDeleteTarget(null);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [deleteTarget]);

  function clearFileInput() {
    if (inputRef.current) inputRef.current.value = "";
  }

  function onFileSelected(file: File) {
    setError(null);
    setProgress(null);
    setFailure(null);

    const problem = validateUploadFile(file);
    if (problem) {
      // Reject before requesting a presigned URL so nothing invalid reaches storage.
      setRejection(problem);
      setPendingFile(null);
      clearFileInput();
      return;
    }

    setRejection(null);
    setPendingFile(file);
    void onUpload(file);
  }

  async function onUpload(file: File) {
    const controller = new AbortController();
    abortRef.current = controller;

    setBusy(true);
    setError(null);
    setFailure(null);
    setTransfer({ loaded: 0, total: file.size, percent: 0 });
    setProgress(`Requesting upload link for ${file.name}…`);

    try {
      const safeName = file.name.replace(SAFE_KEY, "-");
      const contentType = file.type || "application/octet-stream";

      const { uploadUrl, key } = await getUploadUrl({
        data: { key: `${Date.now()}-${safeName}`, contentType },
      });

      setProgress(`Uploading ${file.name}…`);
      await uploadWithProgress({
        url: uploadUrl,
        file,
        contentType,
        signal: controller.signal,
        onProgress: setTransfer,
      });

      setProgress(`Uploaded ${key}`);
      if (account.userId) {
        await recordUploadNotification({
          userId: account.userId,
          fileName: file.name,
          key,
          size: file.size,
        });
      }
      setPendingFile(null);
      setTransfer(null);
      clearFileInput();
      await refresh();
    } catch (cause) {
      setProgress(null);
      setTransfer(null);
      const described = describeUploadFailure(cause);
      setFailure(described);
      if (!described.retryable) setPendingFile(null);
    } finally {
      abortRef.current = null;
      setBusy(false);
    }
  }

  function onRetry() {
    if (pendingFile) void onUpload(pendingFile);
  }

  function onCancelUpload() {
    abortRef.current?.abort();
  }

  async function onOpen(key: string) {
    setError(null);
    try {
      const { url } = await getDownloadUrl({ data: { key } });
      window.open(url, "_blank", "noopener,noreferrer");
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Could not create a download link.");
    }
  }

  /** Clipboard write with a hidden-textarea fallback for non-secure contexts. */
  async function writeClipboard(value: string) {
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(value);
        return true;
      }
    } catch {
      // Fall through to the legacy path below.
    }
    try {
      const area = document.createElement("textarea");
      area.value = value;
      area.setAttribute("readonly", "");
      area.style.position = "fixed";
      area.style.opacity = "0";
      document.body.appendChild(area);
      area.select();
      const ok = document.execCommand("copy");
      document.body.removeChild(area);
      return ok;
    } catch {
      return false;
    }
  }

  function flagCopied(key: string, label: string) {
    setCopied({ key, label });
    if (copiedTimer.current) clearTimeout(copiedTimer.current);
    copiedTimer.current = setTimeout(() => setCopied(null), 2000);
  }

  async function onCopyKey(key: string) {
    setError(null);
    const ok = await writeClipboard(key);
    if (ok) flagCopied(key, "Path copied");
    else setError("Could not copy to the clipboard. Copy the key manually.");
  }

  /** Copies a presigned link. Storage is private, so links expire in 15 minutes. */
  async function onCopyLink(key: string) {
    setError(null);
    setCopyingLink(key);
    try {
      const { url } = await getDownloadUrl({ data: { key } });
      const ok = await writeClipboard(url);
      if (ok) flagCopied(key, "Link copied (expires in 15 min)");
      else setError("Could not copy to the clipboard. Use Open instead.");
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Could not create a shareable link.");
    } finally {
      setCopyingLink(null);
    }
  }

  async function onDelete(key: string) {
    setDeleteTarget(null);
    setBusy(true);
    setError(null);
    try {
      await removeFile({ data: { key } });
      setProgress(`Deleted ${key}`);
      await refresh();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Delete failed.");
    } finally {
      setBusy(false);
    }
  }

  if (account.loading) {
    return (
      <main className="flex min-h-screen items-center justify-center px-6">
        <p className="text-brand-navy/60">Loading storage…</p>
      </main>
    );
  }

  return (
    <main className="min-h-screen px-6 pt-24 md:pt-32 pb-28">
      <div className="mx-auto max-w-5xl">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="text-xs font-bold uppercase tracking-widest text-brand-accent">
              {account.isAdmin ? "Admin console" : "Member area"}
            </p>
            <h1 className="mt-3 font-display text-4xl font-bold text-brand-navy">File storage</h1>
            <p className="mt-2 max-w-xl text-sm text-brand-navy/60">
              {account.isAdmin
                ? "Upload files and browse every stored object. Links are presigned and expire after 15 minutes."
                : "Upload files to your private folder. Only you and administrators can open them."}
            </p>
          </div>
          <div className="flex flex-wrap gap-3">
            {account.isAdmin && (
              <Link
                to="/admin"
                className="rounded-xl border border-brand-navy/15 px-5 py-3 text-sm font-bold text-brand-navy hover:border-brand-accent hover:text-brand-accent"
              >
                Admin console
              </Link>
            )}
            <Link
              to="/dashboard"
              className="rounded-xl border border-brand-navy/15 px-5 py-3 text-sm font-bold text-brand-navy hover:border-brand-accent hover:text-brand-accent"
            >
              My dashboard
            </Link>
          </div>
        </div>

        <section className="mt-10 rounded-2xl border border-brand-navy/10 bg-background p-6">
          <h2 className="font-display text-xl font-bold text-brand-navy">Upload a file</h2>
          <p className="mt-1 text-sm text-brand-navy/60">
            The browser requests a presigned URL from the server, then uploads directly to storage.
          </p>
          <div className="mt-4 flex flex-wrap items-center gap-3">
            <input
              ref={inputRef}
              type="file"
              disabled={busy}
              accept={UPLOAD_ACCEPT_ATTRIBUTE}
              aria-describedby="upload-requirements"
              onChange={(event) => {
                const file = event.target.files?.[0];
                if (file) onFileSelected(file);
              }}
              className="block w-full max-w-sm text-sm text-brand-navy file:mr-4 file:rounded-xl file:border-0 file:bg-brand-navy file:px-5 file:py-3 file:text-sm file:font-bold file:text-background hover:file:bg-brand-accent"
            />
            <button
              type="button"
              onClick={() => void refresh()}
              disabled={busy}
              className="rounded-xl border border-brand-navy/15 px-5 py-3 text-sm font-bold text-brand-navy hover:border-brand-accent hover:text-brand-accent disabled:opacity-50"
            >
              Refresh list
            </button>
          </div>

          <div
            id="upload-requirements"
            className="mt-4 rounded-xl border border-brand-navy/10 bg-brand-navy/5 px-4 py-3 text-xs text-brand-navy/70"
          >
            <p className="font-bold uppercase tracking-widest text-brand-navy/50">
              Accepted uploads
            </p>
            <ul className="mt-2 space-y-1">
              {ALLOWED_UPLOAD_GROUPS.map((group) => (
                <li key={group.label}>
                  <span className="font-semibold text-brand-navy">{group.label}:</span>{" "}
                  {group.extensions.map((extension) => `.${extension}`).join(", ")}
                </li>
              ))}
              <li>
                <span className="font-semibold text-brand-navy">Maximum size:</span>{" "}
                {formatBytes(MAX_UPLOAD_BYTES)} per file
              </li>
            </ul>
          </div>

          {rejection && (
            <div
              role="alert"
              className="mt-3 rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900"
            >
              <p className="font-bold">{rejection.title}</p>
              <p className="mt-1">{rejection.reason}</p>
              <p className="mt-1 text-amber-800">{rejection.hint}</p>
              <button
                type="button"
                onClick={() => setRejection(null)}
                className="mt-2 text-xs font-bold uppercase tracking-widest text-amber-900 hover:underline"
              >
                Dismiss
              </button>
            </div>
          )}

          {transfer && (
            <div className="mt-4 rounded-xl border border-brand-navy/10 bg-brand-navy/5 px-4 py-3">
              <div className="flex flex-wrap items-baseline justify-between gap-2 text-sm">
                <span className="font-semibold text-brand-navy">
                  {pendingFile ? pendingFile.name : "Uploading"}
                </span>
                <span className="text-brand-navy/60">
                  {formatBytes(transfer.loaded)} / {formatBytes(transfer.total)} ·{" "}
                  {transfer.percent}%
                </span>
              </div>
              <div
                role="progressbar"
                aria-label="Upload progress"
                aria-valuemin={0}
                aria-valuemax={100}
                aria-valuenow={transfer.percent}
                className="mt-2 h-2 w-full overflow-hidden rounded-full bg-brand-navy/10"
              >
                <div
                  className="h-full rounded-full bg-brand-accent transition-[width] duration-200 ease-out"
                  style={{ width: `${transfer.percent}%` }}
                />
              </div>
              {busy && (
                <button
                  type="button"
                  onClick={onCancelUpload}
                  className="mt-3 text-xs font-bold uppercase tracking-widest text-brand-navy/60 hover:text-red-600"
                >
                  Cancel upload
                </button>
              )}
            </div>
          )}

          {failure && (
            <div
              role="alert"
              className="mt-3 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800"
            >
              <p className="font-bold">{failure.title}</p>
              <p className="mt-1">{failure.reason}</p>
              <p className="mt-1 text-red-700">{failure.hint}</p>
              <div className="mt-3 flex flex-wrap gap-3">
                {failure.retryable && pendingFile && (
                  <button
                    type="button"
                    onClick={onRetry}
                    disabled={busy}
                    className="rounded-xl bg-brand-navy px-4 py-2 text-xs font-bold uppercase tracking-widest text-background hover:bg-brand-accent disabled:opacity-50"
                  >
                    Retry upload
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => {
                    setFailure(null);
                    setPendingFile(null);
                    clearFileInput();
                  }}
                  className="text-xs font-bold uppercase tracking-widest text-red-800 hover:underline"
                >
                  Dismiss
                </button>
              </div>
            </div>
          )}

          {progress && !error && !failure && (
            <p className="mt-3 text-sm text-brand-accent">{progress}</p>
          )}
          {error && (
            <p role="alert" className="mt-3 rounded-xl bg-red-50 px-4 py-3 text-sm text-red-700">
              {error}
            </p>
          )}
        </section>

        {account.isAdmin && (
          <div className="mt-6 flex flex-wrap items-center gap-3">
            <input
              value={prefix}
              onChange={(event) => setPrefix(event.target.value)}
              placeholder="Filter by key prefix, e.g. u/<user-id>/"
              className="w-full max-w-md rounded-xl border border-brand-navy/15 bg-background px-4 py-3 text-sm text-brand-navy outline-none focus:border-brand-accent"
            />
            <button
              type="button"
              onClick={() => void refresh()}
              className="rounded-xl border border-brand-navy/15 px-5 py-3 text-sm font-bold text-brand-navy hover:border-brand-accent hover:text-brand-accent"
            >
              Apply filter
            </button>
          </div>
        )}

        <div className="mt-6 flex flex-wrap items-center gap-3">
          <div className="relative w-full max-w-md">
            <input
              type="search"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Search filenames…"
              aria-label="Search files by filename"
              className="w-full rounded-xl border border-brand-navy/15 bg-background px-4 py-3 pr-20 text-sm text-brand-navy outline-none focus:border-brand-accent"
            />
            {query && (
              <button
                type="button"
                onClick={() => {
                  setQuery("");
                  void refresh({ q: "" });
                }}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-xs font-bold uppercase tracking-widest text-brand-navy/50 hover:text-brand-accent"
              >
                Clear
              </button>
            )}
          </div>
          {query.trim() && !loading && (
            <p className="text-sm text-brand-navy/60">
              {totalKnown !== null
                ? `${totalKnown} match${totalKnown === 1 ? "" : "es"}`
                : "Showing matches"}{" "}
              for “{query.trim()}”
            </p>
          )}
        </div>

        <div className="mt-4 flex flex-wrap items-center gap-3">
          <label className="text-xs font-bold uppercase tracking-widest text-brand-navy/50">
            Sort
            <select
              value={sort}
              onChange={(event) => {
                const next = event.target.value as SortKey;
                setSort(next);
                void refresh({ sort: next });
              }}
              className="ml-2 rounded-xl border border-brand-navy/15 bg-background px-3 py-2 text-sm font-medium normal-case tracking-normal text-brand-navy outline-none focus:border-brand-accent"
            >
              {SORT_LABELS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>

          <label className="text-xs font-bold uppercase tracking-widest text-brand-navy/50">
            Per page
            <select
              value={pageSize}
              onChange={(event) => {
                const next = Number(event.target.value);
                setPageSize(next);
                void refresh({ pageSize: next });
              }}
              className="ml-2 rounded-xl border border-brand-navy/15 bg-background px-3 py-2 text-sm font-medium normal-case tracking-normal text-brand-navy outline-none focus:border-brand-accent"
            >
              {[10, 25, 50, 100].map((size) => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </select>
          </label>

          <span className="text-sm text-brand-navy/60">
            Page {page}
            {totalKnown !== null && ` of ${Math.max(1, Math.ceil(totalKnown / pageSize))}`}
            {capped && " · showing the first 2,000 keys"}
          </span>
        </div>

        {listError && (
          <div
            role="alert"
            className="mt-6 flex flex-wrap items-start justify-between gap-4 rounded-2xl border border-red-200 bg-red-50 p-5"
          >
            <div className="min-w-0">
              <p className="font-bold text-red-700">Couldn’t load your files</p>
              <p className="mt-1 text-sm text-red-700/80">{listError}</p>
              {hasLoadedOnce && (
                <p className="mt-1 text-xs text-red-700/70">
                  Showing the last results that loaded successfully.
                </p>
              )}
            </div>
            <button
              type="button"
              onClick={retryLoad}
              disabled={loading}
              className="rounded-xl border border-red-300 px-4 py-2 text-sm font-bold text-red-700 hover:bg-red-100 disabled:opacity-50"
            >
              {loading ? "Retrying…" : "Try again"}
            </button>
          </div>
        )}

        <section className="mt-6 overflow-hidden rounded-2xl border border-brand-navy/10">
          <table className="w-full text-left text-sm">
            <thead className="bg-brand-navy/5 text-xs font-bold uppercase tracking-widest text-brand-navy/50">
              <tr>
                <th className="px-5 py-4">Key</th>
                <th className="px-5 py-4">Size</th>
                <th className="px-5 py-4">Modified</th>
                <th className="px-5 py-4 text-right">Actions</th>
              </tr>
            </thead>
            <tbody aria-busy={loading}>
              {loading &&
                Array.from({ length: Math.min(pageSize, 8) }).map((_, index) => (
                  <tr key={`skeleton-${index}`} className="border-t border-brand-navy/10">
                    <td className="px-5 py-4">
                      <span className="block h-4 w-3/4 animate-pulse rounded bg-brand-navy/10" />
                      <span className="sr-only">Loading files…</span>
                    </td>
                    <td className="px-5 py-4">
                      <span className="block h-4 w-16 animate-pulse rounded bg-brand-navy/10" />
                    </td>
                    <td className="px-5 py-4">
                      <span className="block h-4 w-28 animate-pulse rounded bg-brand-navy/10" />
                    </td>
                    <td className="px-5 py-4">
                      <div className="flex justify-end gap-3">
                        <span className="block h-4 w-10 animate-pulse rounded bg-brand-navy/10" />
                        <span className="block h-4 w-14 animate-pulse rounded bg-brand-navy/10" />
                        <span className="block h-4 w-12 animate-pulse rounded bg-brand-navy/10" />
                      </div>
                    </td>
                  </tr>
                ))}
              {!loading && listError && objects.length === 0 && (
                <tr>
                  <td colSpan={4} className="px-5 py-8 text-center text-brand-navy/50">
                    Storage listing is unavailable right now.
                  </td>
                </tr>
              )}

              {!loading && !listError && objects.length === 0 && (
                <tr>
                  <td colSpan={4} className="px-5 py-8 text-center text-brand-navy/50">
                    No files stored yet. Upload one above to validate storage.
                  </td>
                </tr>
              )}
              {!loading &&
                objects.map((object) => (
                  <tr key={object.key} className="border-t border-brand-navy/10">
                    <td className="max-w-xs px-5 py-4 font-medium text-brand-navy">
                      <span className="block truncate" title={object.key}>
                        {object.key}
                      </span>
                      {copied?.key === object.key && (
                        <span
                          role="status"
                          className="mt-1 block text-xs font-bold text-brand-accent"
                        >
                          {copied.label}
                        </span>
                      )}
                    </td>
                    <td className="px-5 py-4 text-brand-navy/70">{formatBytes(object.size)}</td>
                    <td className="px-5 py-4 text-brand-navy/70">
                      {formatDate(object.lastModified)}
                    </td>
                    <td className="px-5 py-4">
                      <div className="flex flex-wrap items-center justify-end gap-x-3 gap-y-1">
                        <button
                          type="button"
                          onClick={() => void onOpen(object.key)}
                          className="text-sm font-bold text-brand-accent hover:underline"
                        >
                          Open
                        </button>
                        <button
                          type="button"
                          onClick={() => void onCopyKey(object.key)}
                          className="text-sm font-bold text-brand-navy/70 hover:text-brand-accent hover:underline"
                        >
                          Copy path
                        </button>
                        <button
                          type="button"
                          onClick={() => void onCopyLink(object.key)}
                          disabled={copyingLink === object.key}
                          className="text-sm font-bold text-brand-navy/70 hover:text-brand-accent hover:underline disabled:opacity-50"
                        >
                          {copyingLink === object.key ? "Copying…" : "Copy link"}
                        </button>
                        <button
                          type="button"
                          onClick={() => setDeleteTarget(object)}
                          disabled={busy}
                          className="text-sm font-bold text-red-600 hover:underline disabled:opacity-50"
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </section>

        <div className="mt-4 flex items-center justify-between gap-3">
          <button
            type="button"
            onClick={() => void load(page - 1)}
            disabled={loading || page <= 1}
            className="rounded-xl border border-brand-navy/15 px-5 py-3 text-sm font-bold text-brand-navy hover:border-brand-accent hover:text-brand-accent disabled:opacity-40"
          >
            Previous
          </button>
          <span className="text-sm text-brand-navy/60">
            {objects.length} file{objects.length === 1 ? "" : "s"} on this page
          </span>
          <button
            type="button"
            onClick={() => void load(page + 1)}
            disabled={loading || !hasNext}
            className="rounded-xl border border-brand-navy/15 px-5 py-3 text-sm font-bold text-brand-navy hover:border-brand-accent hover:text-brand-accent disabled:opacity-40"
          >
            Next
          </button>
        </div>

        {deleteTarget && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-brand-navy/50 px-6"
            role="dialog"
            aria-modal="true"
            aria-labelledby="delete-file-title"
            onClick={() => setDeleteTarget(null)}
          >
            <div
              className="w-full max-w-md rounded-2xl border border-brand-navy/10 bg-background p-6 shadow-xl"
              onClick={(event) => event.stopPropagation()}
            >
              <h2 id="delete-file-title" className="font-display text-xl font-bold text-brand-navy">
                Delete this file?
              </h2>
              <p className="mt-2 break-all rounded-xl bg-brand-navy/5 px-4 py-3 text-sm font-medium text-brand-navy">
                {deleteTarget.key}
              </p>
              <p className="mt-3 text-sm text-brand-navy/60">
                {formatBytes(deleteTarget.size)} · modified {formatDate(deleteTarget.lastModified)}.
                This permanently removes the object from storage and cannot be undone.
              </p>
              <div className="mt-6 flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setDeleteTarget(null)}
                  className="rounded-xl border border-brand-navy/15 px-5 py-3 text-sm font-bold text-brand-navy hover:border-brand-accent hover:text-brand-accent"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={() => void onDelete(deleteTarget.key)}
                  disabled={busy}
                  className="rounded-xl bg-red-600 px-5 py-3 text-sm font-bold text-white hover:bg-red-700 disabled:opacity-50"
                >
                  {busy ? "Deleting…" : "Delete permanently"}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </main>
  );
}
